import { Box, Divider, Grid2, Paper, styled, Typography } from "@mui/material";
import React, { useEffect, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Lottie from "react-lottie";
import PurpleBlinkingLottie from "../../../../Components/Lotties/PurpleBlinkingLottie.json";
import BatteryOverviewIndicator from "../../../DigitalTwinV3/Tabs/BatteryOverview/BatteryOverviewIndicator";
import BatteryOverviewKeyMetric from "../../../DigitalTwinV3/Tabs/BatteryOverview/BatteryOverviewKeyMetric";
import { useBatteryInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import { getBatteryIndicators } from "../../../../Api/DigitalTwin";
import { usePagesContent } from "../../../../Hooks/usePagesContent";
import { getDigitalTwinList } from "../../../../Api/DigitalTwinListApi";

const OverviewContainer = styled(Box)(() => ({
    marginTop: "20px",
    width: "100%",
    minHeight: "200px",
}));

/**
 * Custom platform Battery Overview tab.
 *
 * Respects org meta config stored in metaInfo:
 *   - customOverviewSections  : which sections to render (from overview.sections)
 *   - batteryIndicators       : filtered to overview.indicators label list by buildCustomMetaInfo
 *   - batteryKeyMetrics       : filtered to overview.keyMetrics label list by buildCustomMetaInfo
 */
export default function CustomBatteryOverview() {
    const { t } = useTranslation(["common", "digital_twin"]);
    const { batteryID } = useBatteryInfo();
    const [indicatorsData, setIndicatorsData] = useState({});
    const [keyMetricsData, setKeyMetricsData] = useState({});
    const { metaInfo: pageMetaInfo = {} } = usePagesContent();

    const customOverviewSections = pageMetaInfo.customOverviewSections || [];

    // When sections list is empty (no restriction), show all; otherwise respect the list
    const showIndicators =
        customOverviewSections.length === 0 || customOverviewSections.includes("batteryIndicators");
    const showKeyMetrics =
        customOverviewSections.length === 0 || customOverviewSections.includes("batteryKeyMetrics");

    const lottieOptions = {
        loop: true,
        autoplay: true,
        animationData: PurpleBlinkingLottie,
        rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
    };

    const fetchBatteryOverviewData = useCallback(async () => {
        try {
            const keyMetricKeys = (pageMetaInfo.batteryKeyMetrics ?? []).map((m) => m.key);
            const needsBmsForSpeed = keyMetricKeys.includes("ext_wb_vehicle_speed") && !keyMetricKeys.includes("batteryStatus");
            const keyMetricColumns = needsBmsForSpeed ? [...keyMetricKeys, "batteryStatus"] : keyMetricKeys;
            const requests = [
                showIndicators ? getBatteryIndicators(batteryID, pageMetaInfo.indicatorProfile) : Promise.resolve(null),
                showKeyMetrics ? getDigitalTwinList({
                    pageNumber: 1,
                    rowsPerPage: 1,
                    filters: { batteryID: [batteryID] },
                    ...(keyMetricColumns.length > 0 && { columns: keyMetricColumns }),
                }) : Promise.resolve(null),
            ];
            const [indicatorsRes, keyMetricsRes] = await Promise.all(requests);
            if (indicatorsRes) setIndicatorsData(indicatorsRes.response.data ?? {});
            if (keyMetricsRes) setKeyMetricsData(keyMetricsRes.response.data[0] ?? {});
        } catch (error) {
            console.error("CustomBatteryOverview: error fetching data:", error);
        }
    }, [batteryID, showIndicators, showKeyMetrics, pageMetaInfo.batteryKeyMetrics, pageMetaInfo.indicatorProfile]);

    useEffect(() => {
        fetchBatteryOverviewData();
    }, [fetchBatteryOverviewData]);

    const hasIndicators = (pageMetaInfo.batteryIndicators ?? []).length > 0;
    const hasDividerBetweenIndicatorsAndKeyMetrics = showIndicators && hasIndicators && showKeyMetrics;

    return (
        <>
            <Grid2 container flexDirection="column" gap="4px" paddingTop="20px" paddingLeft="12px">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box>
                        <Lottie options={lottieOptions} height={20} width={20} />
                    </Box>
                    <Typography variant="fw500s16lh20c000000">
                        {t("digital_twin:overview_tab.title")}
                    </Typography>
                </Box>
                <Typography variant="fw400s12c686868">
                    {t("digital_twin:overview_tab.subtitle")}
                </Typography>
            </Grid2>
            <Grid2 sx={{ mb: 10 }}>
                <OverviewContainer>
                    <Paper
                        elevation={0}
                        sx={{
                            px: 5,
                            py: 4,
                            height: "100%",
                            borderRadius: "8px",
                            border: "1px solid #E6E6E6",
                        }}
                    >
                        {showIndicators && hasIndicators && (
                            <Grid2 container flexDirection="column" gap="16px">
                                <Typography variant="fw500s16lh24c000000">
                                    {t("digital_twin:indicators")}
                                </Typography>
                                <Grid2 container gap="8px">
                                    {(pageMetaInfo.batteryIndicators ?? []).map((indicator) => (
                                        <BatteryOverviewIndicator
                                            key={indicator.key}
                                            name={indicator.label}
                                            isOn={(indicatorsData[indicator.key] ?? "").toLowerCase() === "on"}
                                        />
                                    ))}
                                </Grid2>
                            </Grid2>
                        )}

                        {hasDividerBetweenIndicatorsAndKeyMetrics && (
                            <Divider sx={{ my: 2, borderColor: "#E9E9E9" }} />
                        )}

                        {showKeyMetrics && (
                            <Grid2 container flexDirection="column" gap="16px" sx={{ mb: 2 }}>
                                <Typography variant="fw500s16lh24c000000">
                                    {t("digital_twin:key_metrics")}
                                </Typography>
                                <Grid2 container gap="8px">
                                    {(pageMetaInfo.batteryKeyMetrics ?? []).map((keyMetric) => {
                                        const rawValue = keyMetricsData[keyMetric.key];
                                        const displayValue = (() => {
                                            if (keyMetric.key !== "ext_wb_vehicle_speed") return rawValue;
                                            const speed = parseFloat(rawValue);
                                            if (isNaN(speed)) return rawValue;
                                            const isDischarging = keyMetricsData.batteryStatus === "Discharging";
                                            return (!isDischarging || speed > 250) ? 0 : rawValue;
                                        })();
                                        return (
                                            <BatteryOverviewKeyMetric
                                                key={keyMetric.key}
                                                keyMetric={keyMetric}
                                                value={displayValue}
                                                isAlert={false}
                                                width="220px"
                                            />
                                        );
                                    })}
                                </Grid2>
                            </Grid2>
                        )}


                    </Paper>
                </OverviewContainer>
            </Grid2>
        </>
    );
}
